import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiPackage, FiDollarSign } from 'react-icons/fi';
import './salesReturn.css';

const ReturnPage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [refundMethod, setRefundMethod] = useState('cash');
  const [refundBankAccountId, setRefundBankAccountId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!invoiceId) {
      setError('Missing invoice ID');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch invoice with all items
        const invoiceRes = await axios.get(`/sales/invoice/${invoiceId}`);

        if (!invoiceRes.data) {
          throw new Error('Invoice not found');
        }

        setInvoice(invoiceRes.data);
        // Default the refund method to how the sale was originally paid —
        // the common case — but the user can still change it (e.g. bank
        // sale refunded from the counter in cash).
        setRefundMethod(invoiceRes.data.payment_type === 'bank' ? 'bank' : 'cash');
        setRefundBankAccountId(invoiceRes.data.bank_account_id ? String(invoiceRes.data.bank_account_id) : '');

        // Initialize return quantities for each item
        const initialReturnItems = invoiceRes.data.items.map(item => ({
          sale_id: item.sale_id,
          item_id: item.item_id,
          item_name: item.item_name,
          unit: item.unit,
          max_quantity: item.quantity,
          return_quantity: '', // Default to full quantity
          selling_price: item.normalized_price,
          total_amount: item.total_amount

        }));
        setReturnItems(initialReturnItems);

        const bankAccountsRes = await axios.get('/bank-accounts');
        setBankAccounts(Array.isArray(bankAccountsRes.data) ? bankAccountsRes.data : []);
      } catch (err) {
        console.error('Fetch error:', err);

        setError(err.response?.data?.error || err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [invoiceId]);

  const handleQuantityChange = (saleId, newQuantity) => {
    setReturnItems(prevItems =>
      prevItems.map(item =>
        item.sale_id === saleId
          ? {
              ...item,
              return_quantity: Math.min(item.max_quantity, Math.max(0, newQuantity))
            }
          : item
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Process returns for all items with quantity > 0
      const returns = returnItems
  .filter(item => item.return_quantity > 0)
  .map(item => {
    let normalizedQty = item.return_quantity;

    if (item.unit === 'set') {
      normalizedQty = item.return_quantity * 6;
    } else if (item.unit === 'dozen') {
      normalizedQty = item.return_quantity * 12;
    } else if (item.unit === 'piece' || item.unit === 'kg') {
      normalizedQty = item.return_quantity * 1;
    }

    return {
      sale_id: item.sale_id,
      quantity: normalizedQty
    };
  });

      if (returns.length === 0) {
        throw new Error('No items selected for return');
      }

      if (refundMethod === 'bank' && !refundBankAccountId) {
        throw new Error('Please select a bank account for a bank refund.');
      }

      const response = await axios.post(
        `/sales/${invoiceId}/return-multiple`,
        {
          returns,
          refund_method: refundMethod,
          bank_account_id: refundMethod === 'bank' ? parseInt(refundBankAccountId) : null
        }
      );

      setSuccess({
        message: 'Returns processed successfully!',
        details: response.data
      });

      setTimeout(() => navigate(`/sales`), 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to process returns');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading invoice details...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!invoice) return <div className="error">Invoice not found</div>;

  // Mirrors the backend's proration formula (return-multiple in sales.js):
  // refunds are paid out at what the customer actually paid, not the
  // pre-discount sticker price, so this preview must apply the same rate.
  const invoiceSubtotal = Number(invoice.subtotal_amount) || 0;
  const discountRate = invoiceSubtotal > 0
    ? (Number(invoice.total_amount) || 0) / invoiceSubtotal
    : 1;
  const discountAmount = Number(invoice.discount_amount) || 0;

  const rawRefund = returnItems.reduce(
    (sum, item) => sum + (item.return_quantity * item.selling_price),
    0
  );
  const totalRefund = Math.round(rawRefund * discountRate * 100) / 100;

  const paidAmount = Number(invoice.paid_amount) || 0;
  const totalAmount = Number(invoice.total_amount) || 0;

  // total_amount/paid_amount are per-invoice fields regardless of whether a
  // retailer is attached, so every invoice settles a return against its own
  // remaining balance first, and only pays out cash/bank for whatever's left
  // over — see docs/superpowers/specs/2026-08-16-return-credit-first-refund-design.md.
  const remainingBefore = Math.max(0, Math.round((totalAmount - paidAmount) * 100) / 100);
  const settled = Math.min(totalRefund, remainingBefore);
  const physicalRefund = Math.round((totalRefund - settled) * 100) / 100;
  const remainingAfter = Math.round((remainingBefore - settled) * 100) / 100;

  return (
    <div className="return-container">
      <button onClick={() => navigate(`/sales`)} className="back-button">
        <FiArrowLeft /> Back to Sales
      </button>

      <h1>Process Return for Invoice #{invoice.invoice_no}</h1>

      <div className="invoice-summary">
        <h3>Invoice Summary</h3>
        <p><strong>Retailer:</strong> {invoice.retailer_name || 'Walk-in Customer'}</p>
        {discountAmount > 0 && (
          <>
            <p><strong>Subtotal:</strong> Rs {invoiceSubtotal.toFixed(2)}</p>
            <p><strong>Discount:</strong> -Rs {discountAmount.toFixed(2)}</p>
          </>
        )}
        <p><strong>Invoice Total:</strong> Rs {Number(invoice.total_amount).toFixed(2)}</p>
        <p><strong>Amount Paid:</strong> Rs {Number(invoice.paid_amount).toFixed(2)}</p>
        {remainingBefore > 0 && (
          <p><strong>Remaining Due:</strong> Rs {remainingBefore.toFixed(2)}</p>
        )}
        <p>
          <strong>Payment Method:</strong>{' '}
          {invoice.payment_type === 'bank' ? '🏦 Bank / Transfer' :
           invoice.payment_type === 'cheque' ? '📋 Cheque' : '💵 Cash'}
        </p>
        {invoice.retailer_name && (
          <p><strong>Credit Amount:</strong> Rs {(Number(invoice.total_amount) - Number(invoice.paid_amount)).toFixed(2)}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="return-form">
        <h3>Items to Return</h3>

        <div className="return-items-list">
          {returnItems.map((item) => (
            <div key={item.sale_id} className="return-item">
              <div className="item-info">
                <h4>{item.item_name}</h4>
                <p>Original Qty: {item.max_quantity} {item.unit}</p>
                <p>Sold Price: Rs {Number(item.selling_price).toFixed(2)}</p>
                <p>Total: Rs {Number(item.total_amount)}</p>
              </div>


              <div className="return-controls">
                <label>
                  Quantity to Return:
                  <input
                    type="number"
                    min="0"
                    max={item.max_quantity}
                    value={item.return_quantity}
                    onChange={(e) => handleQuantityChange(
                      item.sale_id,
                      parseInt(e.target.value) || 0
                    )}
                  />
                </label>
                <div className="item-refund">
                  Refund: Rs {(item.return_quantity * item.selling_price * discountRate).toFixed(2)}
                  {discountRate < 1 && (
                    <span className="refund-raw-price"> (Rs {(item.return_quantity * item.selling_price).toFixed(2)} before discount)</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {physicalRefund > 0 && (
          <div className="refund-method-section">
            <h3>How is this refund being paid out?</h3>
            <div className="form-group">
              <label>
                <input
                  type="radio"
                  name="refundMethod"
                  value="cash"
                  checked={refundMethod === 'cash'}
                  onChange={() => setRefundMethod('cash')}
                />
                {' '}💵 Cash (from the counter)
              </label>
              <label>
                <input
                  type="radio"
                  name="refundMethod"
                  value="bank"
                  checked={refundMethod === 'bank'}
                  onChange={() => setRefundMethod('bank')}
                />
                {' '}🏦 Bank transfer
              </label>
            </div>

            {refundMethod === 'bank' && (
              <div className="form-group">
                <label>
                  Bank Account:
                  <select
                    value={refundBankAccountId}
                    onChange={(e) => setRefundBankAccountId(e.target.value)}
                  >
                    <option value="">Select bank account</option>
                    {bankAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_name} - {account.account_number}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        )}

        <div className="totals-section">
          <div className="form-group">
            <label>
              Total Refund Amount:
              <div className="amount-display">
                <FiDollarSign /> Rs {totalRefund.toFixed(2)}
              </div>
            </label>
          </div>

          {remainingBefore > 0 && (
            <div className="form-group">
              <label>
                Remaining Due (after this return):
                <div className="amount-display">
                  <FiDollarSign /> Rs {remainingBefore.toFixed(2)} → Rs {remainingAfter.toFixed(2)}
                </div>
              </label>
            </div>
          )}

          {physicalRefund > 0 && (
            <div className="form-group">
              <label>
                {refundMethod === 'bank' ? 'Bank Withdrawal:' : 'Cash Out:'}
                <div className="amount-display">
                  <FiDollarSign /> Rs {physicalRefund.toFixed(2)}
                </div>
              </label>
            </div>
          )}

          <div className="form-group">
            <label>
              Inventory Update:
              <div className="amount-display">
                <FiPackage /> {returnItems.reduce((sum, item) => sum + item.return_quantity, 0)} units
              </div>
            </label>
          </div>

          {invoice.retailer_name && (
            <div className="form-group">
              <label>
                Retailer Credit Reduction:
                <div className="amount-display">
                  <FiDollarSign /> Rs {totalRefund.toFixed(2)}
                </div>
              </label>
            </div>
          )}
        </div>

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Processing...' : 'Submit Returns'}
        </button>

        {success && (
          <div className="success-message">
            <p>{success.message}</p>
            {success.details.processedReturns.map((ret, index) => (
              <div key={index}>
                <p>{ret.item_name}: {ret.quantity_returned} units (Rs {ret.refund_amount.toFixed(2)})</p>
                {ret.remaining_quantity > 0 ? (
                  <p>Remaining quantity: {ret.remaining_quantity}</p>
                ) : (
                  <p>Item fully returned</p>
                )}
              </div>
            ))}
          </div>
        )}
      </form>
    </div>
  );
};

export default ReturnPage;
